import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { GameState, LayerState, LayerType } from '../types/game';

const LAYER_TYPES: LayerType[] = ['outer-world', 'snake', 'breakout', 'reaction'];

function makeLayer(depth: number): LayerState {
  return {
    type: LAYER_TYPES[depth] ?? 'reaction',
    score: 0,
    isComplete: false,
    canDiveAt: null,
  };
}

interface GameActions {
  startGame: () => void;
  diveIn: () => void;
  ascend: () => void;
  addScore: (points: number) => void;
  gameOver: () => void;
  winGame: () => void;
  resetGame: () => void;
}

type GameStore = GameState & GameActions;

const RESET_STATE: Omit<GameState, 'highScore' | 'deepestReached' | 'runCount'> = {
  phase: 'idle',
  currentDepth: 0,
  maxDepth: 3,
  layers: [],
  totalScore: 0,
  diveStreak: 0,
  scoreMultiplier: 1,
};

export const useGameStore = create<GameStore>()(
  persist(
    (set, get) => ({
      ...RESET_STATE,
      highScore: 0,
      deepestReached: 0,
      runCount: 0,
      diveStreak: 0,
      scoreMultiplier: 1,

      startGame() {
        const { runCount } = get();
        set({ ...RESET_STATE, phase: 'playing', layers: [makeLayer(0)], runCount: runCount + 1 });
      },

      diveIn() {
        const { currentDepth, maxDepth, layers, deepestReached, diveStreak } = get();
        if (currentDepth >= maxDepth) return;
        const newDepth = currentDepth + 1;
        const newStreak = diveStreak + 1;
        const newMultiplier = Math.min(4, 1 + newStreak * 0.5);
        set({
          phase: 'transitioning',
          currentDepth: newDepth,
          deepestReached: Math.max(deepestReached, newDepth),
          layers: [...layers, makeLayer(newDepth)],
          diveStreak: newStreak,
          scoreMultiplier: newMultiplier,
        });
        setTimeout(() => set({ phase: 'playing' }), 700);
      },

      ascend() {
        const { currentDepth, layers } = get();
        if (currentDepth <= 0) return;
        const topLayer = layers[currentDepth];
        const parentLayer = layers[currentDepth - 1];
        if (!topLayer || !parentLayer) return;
        const newLayers = [
          ...layers.slice(0, currentDepth - 1),
          { ...parentLayer, score: parentLayer.score + topLayer.score },
        ];
        set({
          phase: 'transitioning',
          currentDepth: currentDepth - 1,
          layers: newLayers,
        });
        setTimeout(() => set({ phase: 'playing' }), 700);
      },

      addScore(points: number) {
        const { currentDepth, layers, totalScore, scoreMultiplier } = get();
        const scaled = Math.round(points * scoreMultiplier);
        const newLayers = [...layers];
        const layer = newLayers[currentDepth];
        if (layer) {
          newLayers[currentDepth] = { ...layer, score: layer.score + scaled };
        }
        set({ layers: newLayers, totalScore: totalScore + scaled });
      },

      gameOver() {
        const { totalScore, highScore } = get();
        set({ phase: 'game-over', highScore: Math.max(totalScore, highScore), diveStreak: 0, scoreMultiplier: 1 });
      },

      winGame() {
        const { totalScore, highScore, deepestReached } = get();
        const WIN_BONUS = 500;
        const finalScore = totalScore + WIN_BONUS;
        set({
          phase: 'won',
          totalScore: finalScore,
          highScore: Math.max(finalScore, highScore),
          deepestReached: Math.max(deepestReached, 3),
        });
      },

      resetGame() {
        set({ ...RESET_STATE, phase: 'idle' });
      },
    }),
    {
      name: 'ngc-progress',
      partialize: (state) => ({
        highScore: state.highScore,
        deepestReached: state.deepestReached,
        runCount: state.runCount,
      }),
    },
  ),
);
